/**
 * YamanekoGames Hub - Authentication Error Handling (fail-closed)
 *
 * Sweeper (`luckyanimal-sweeper/src/services/ymgAuth.ts`) のパターンを移植。
 *
 * 目的:
 *   - HTTP/SDK エラーを型付き例外 (ApiError / NetworkError) で伝播させる
 *   - 401（要再認証）を握り潰さず、アプリ層に再ログイン UI を出させる
 *   - 早期に発火した 401 を UI マウント前に失わないようバッファリング
 *
 * 使い分け:
 *   - ymgFetch が 401 を観測 → notifyReauthRequired(error) → throw
 *   - App.tsx は onReauthRequired() でコールバックを登録し、エラー画面に倒す
 *
 * 注意:
 *   - 本プロジェクトの `services/auth.ts` は LINE/Google/Apple の認証状態管理
 *     （signed_in / guest 遷移）で、こちらの ymgAuth とは責務が異なる。
 *   - Sweeper と整合させるためファイル名を `ymgAuth.ts` としている。
 */

// ---------------------------------------------------------------------------
// Error classes
// ---------------------------------------------------------------------------

/**
 * YamanekoGames Hub から返された HTTP エラー。
 *
 * - status: HTTP ステータスコード
 * - body: サーバーがレスポンスボディで返した JSON（エラー詳細）
 * - retryAfterMs: 429 時のリトライ推奨待機時間（ミリ秒）
 * - requiresReauth: true なら UI に再ログインを促すべき 401
 */
export class ApiError extends Error {
  public status: number
  public body: unknown
  public retryAfterMs?: number
  public requiresReauth?: boolean

  constructor(
    status: number,
    body: unknown,
    retryAfterMs?: number,
    requiresReauth?: boolean,
  ) {
    super(`ApiError: ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.body = body
    this.retryAfterMs = retryAfterMs
    this.requiresReauth = requiresReauth
  }
}

/**
 * ネットワーク到達不能エラー（fetch 自体が例外を投げた場合）。
 * 5xx など HTTP レスポンスを得たケースは ApiError を使う。
 */
export class NetworkError extends Error {
  public code: string
  public options?: { originalError?: Error }

  constructor(
    message: string,
    code: string,
    options?: { originalError?: Error },
  ) {
    super(message)
    this.name = 'NetworkError'
    this.code = code
    this.options = options
  }
}

// ---------------------------------------------------------------------------
// Re-auth helpers
// ---------------------------------------------------------------------------

/** 再認証が必要と判定できる ApiError か。 */
export function isReauthRequired(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 401 &&
    error.requiresReauth === true
  )
}

// ---------------------------------------------------------------------------
// Global re-auth callback system (with buffering)
// ---------------------------------------------------------------------------

type ReauthRequiredCallback = (error: ApiError) => void

let globalReauthCallback: ReauthRequiredCallback | null = null

/**
 * コールバック登録前に発生した再認証要求を保持するバッファ。
 * UI マウント前に 401 が起きても、登録時に最新 1 件を届ける。
 */
let pendingReauthEvent: ApiError | null = null

/**
 * 再認証要求時のコールバックを登録する。
 *
 * 登録時に未配信のイベントがあれば、microtask で即座に配信する。
 * これにより App.tsx マウント前の早期 401 が落ちない。
 *
 * @returns 登録解除用の関数
 */
export function onReauthRequired(
  callback: ReauthRequiredCallback,
): () => void {
  globalReauthCallback = callback
  if (pendingReauthEvent) {
    const buffered = pendingReauthEvent
    pendingReauthEvent = null
    Promise.resolve().then(() => {
      try {
        callback(buffered)
      } catch (e) {
        console.warn('[YmgAuth] buffered reauth listener threw:', e)
      }
    })
  }
  return () => {
    if (globalReauthCallback === callback) {
      globalReauthCallback = null
    }
  }
}

/**
 * 再認証要求を通知する。
 *
 * コールバック未登録時はバッファされ、次回登録時に配信される。
 * これは `ymgFetch` / `hubProfile` など低レイヤから呼ばれる想定で、
 * アプリ層の UI に 401 の発生を伝えるためのフック。
 *
 * 登録済みコールバックが throw した場合、イベントが完全に失われると
 * 再ログイン画面が出ない穴が残るため（Red Team 指摘）、バッファへ
 * 再格納する。次の登録者 or 同じ登録者の次回チャンスに配信される。
 */
export function notifyReauthRequired(error: ApiError): void {
  if (globalReauthCallback) {
    try {
      globalReauthCallback(error)
      return
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'unknown'
      console.warn(`[YmgAuth] reauth callback threw: ${msg} — re-buffering event`)
      // fall through to buffer
    }
  }
  pendingReauthEvent = error
}
